import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  ALL_PAGES,
  API_CAPABILITIES,
  type ApiCapability,
  type ApiKeyGrantInput,
} from '@mpr/shared';

import { Capability } from '@mpr/shared';

import { api, ApiError } from '../lib/api.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * The tenant's own account: plan, usage, features, API keys and users.
 *
 * Distinct from the Admin console, which manages *other* tenants. Everything
 * here concerns the account the signed-in user belongs to.
 */
type AccountTab = 'me' | 'overview' | 'users' | 'keys' | 'apps';

/**
 * The account screen, showing only what this person can open.
 *
 * The gap it closes: everything here except a person's own password needs
 * `settings.read`, and the tab itself asked for nothing — so a publisher was
 * offered a Settings section, opened it, and met a panel of errors. The rule
 * the owner stated is the right one: a heading with nothing openable under it
 * should not be there at all.
 *
 * "My account" stays for everybody, because changing your own password is not
 * a permission anybody can be denied without being locked out of their own
 * login — and it is why this tab is not simply hidden from a publisher.
 */
export function AccountPanel() {
  const { t } = useI18n();

  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const held = new Set(me.data?.capabilities ?? []);
  const runsAccount = held.has(Capability.SETTINGS_READ);

  const tabs: Array<{ id: AccountTab; label: TranslationKey }> = [
    { id: 'me', label: 'account.tab.me' },
    ...(runsAccount
      ? ([
          { id: 'overview', label: 'account.tab.overview' },
          { id: 'users', label: 'account.tab.users' },
          { id: 'keys', label: 'account.tab.keys' },
          { id: 'apps', label: 'account.tab.apps' },
        ] as Array<{ id: AccountTab; label: TranslationKey }>)
      : []),
  ];

  // Whoever runs the account opens on the account; everybody else has one page.
  const [tab, setTab] = useState<AccountTab>('me');
  const current = tabs.some((entry) => entry.id === tab) ? tab : 'me';

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('account.title')}</h2>
        {/* One tab is not a choice, so it is not offered as one. */}
        {tabs.length > 1 && (
          <div className="segmented">
            {tabs.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={current === entry.id ? 'active' : ''}
                onClick={() => setTab(entry.id)}
              >
                {t(entry.label)}
              </button>
            ))}
          </div>
        )}
      </div>

      {current === 'me' && <ChangeOwnPassword />}
      {current === 'overview' && (
        <>
          <Overview />
          <ChangeOwnPassword />
        </>
      )}
      {current === 'users' && <Users />}
      {current === 'keys' && <ApiKeys />}
      {current === 'apps' && <MetaApps />}
    </section>
  );
}

/*
 * Names for the catalogue keys the product ships with.
 *
 * The catalogue is editable, so a key may exist that this dictionary has never
 * heard of. Rather than showing a bare `POSTS_PER_DAY`, the API's own `name` is
 * the fallback — translated where we have a translation, in the administrator's
 * own words where we do not. That is what makes an admin-created limit usable
 * in both languages without a redeploy.
 */
const QUOTA_LABELS: Record<string, TranslationKey> = {
  META_ACCOUNTS: 'account.usage.metaAccounts',
  PAGES: 'account.usage.pages',
  USERS: 'account.usage.users',
  META_APPS: 'account.usage.metaApps',
  AGENT_CALLS_PER_MONTH: 'account.usage.agentCalls',
  AUTO_REPLIES_PER_MONTH: 'account.usage.autoReplies',
  POSTS_PER_DAY: 'account.usage.postsPerDay',
  COMMENTS_PER_DAY: 'account.usage.commentsPerDay',
};

const FEATURE_LABELS: Record<string, TranslationKey> = {
  PUBLISHING: 'account.featureName.PUBLISHING',
  COMMENTS: 'account.featureName.COMMENTS',
  COMMENT_REPLIES: 'account.featureName.COMMENT_REPLIES',
  AUTOMATION: 'account.featureName.AUTOMATION',
  PUBLIC_SOURCE: 'account.featureName.PUBLIC_SOURCE',
  API_ACCESS: 'account.featureName.API_ACCESS',
  OWN_META_APP: 'account.featureName.OWN_META_APP',
};

function Overview() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const account = useQuery({ queryKey: ['account'], queryFn: api.account });

  const saveSettings = useMutation({
    mutationFn: (patch: { features?: Record<string, boolean> }) => api.updateAccountSettings(patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account'] }),
  });

  if (account.isLoading) return <div className="loading">{t('common.loading')}</div>;

  const data = account.data!;
  const canEdit = data.role === 'ADMIN' || data.role === 'SUPERADMIN';

  return (
    <>
      <div className="plan-banner">
        <div>
          <span className="muted small">{t('account.currentPlan')}</span>
          <h3 style={{ margin: '2px 0 0' }}>{data.tenant.plan.name}</h3>
        </div>
        <span className={`badge ${data.tenant.status === 'ACTIVE' ? 'ok' : 'warn'}`}>
          {data.tenant.status}
        </span>
      </div>

      {data.warnings.length > 0 && (
        <div className="notice warn">
          {t('account.quotaWarning', {
            list: data.warnings
              .map(
                (w) =>
                  `${label(t, QUOTA_LABELS, w.resource, data.usage[w.resource]?.name)} ${w.used}/${w.limit ?? '∞'}`,
              )
              .join(' · '),
          })}
        </div>
      )}

      <h3 style={{ marginTop: 22 }}>{t('account.usage')}</h3>
      <div className="usage-list">
        {Object.entries(data.usage).map(([key, value]) => {
          // An unlimited quota has no bar to fill: a meter at 0% would read as
          // "none used", and one at 100% as "full". Neither is true, so it
          // shows the count and nothing else.
          const unlimited = value.limit === null;
          const percent =
            !unlimited && value.limit! > 0 ? Math.min(100, (value.used / value.limit!) * 100) : 0;
          const level = percent >= 90 ? 'err' : percent >= 80 ? 'warn' : 'ok';

          return (
            <div key={key} className="usage-row">
              <div className="usage-head">
                <span>
                  {label(t, QUOTA_LABELS, key, value.name)}
                  {value.period !== 'TOTAL' && (
                    <span className="tiny muted">
                      {' '}
                      {t(`account.period.${value.period}` as TranslationKey)}
                    </span>
                  )}
                </span>
                <span className="mono small">
                  {unlimited ? `${value.used} / ∞` : `${value.used} / ${value.limit}`}
                </span>
              </div>
              {!unlimited && (
                <div className="meter">
                  <div className={`meter-fill ${level}`} style={{ width: `${percent}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <h3 style={{ marginTop: 26 }}>{t('account.features')}</h3>
      <p className="muted small">{t('account.featuresHint')}</p>

      <div className="grid-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('account.feature')}</th>
              <th>{t('account.layer.capability')}</th>
              <th>{t('account.layer.entitlement')}</th>
              <th>{t('account.layer.setting')}</th>
              <th>{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.values(data.features).map((feature) => (
              <tr key={feature.key}>
                <td>
                  <strong>{label(t, FEATURE_LABELS, feature.key, feature.name)}</strong>
                  {feature.reason && <div className="tiny muted">{feature.reason}</div>}
                </td>
                <td>
                  <LayerCell ok={feature.capability} />
                </td>
                <td>
                  <LayerCell ok={feature.entitlement} />
                </td>
                <td>
                  {/* Publishing and API access have no meaningful "off": a
                      tenant switching off publishing would be switching off the
                      product. They show state rather than offering a toggle. */}
                  {feature.key === 'PUBLISHING' || feature.key === 'API_ACCESS' ? (
                    <LayerCell ok={feature.setting} />
                  ) : (
                    <input
                      type="checkbox"
                      checked={feature.setting}
                      disabled={!canEdit || !feature.capability || !feature.entitlement}
                      onChange={(event) =>
                        saveSettings.mutate({
                          features: { [feature.key]: event.target.checked },
                        })
                      }
                    />
                  )}
                </td>
                <td>
                  <span className={`badge ${feature.enabled ? 'ok' : 'warn'}`}>
                    {feature.enabled
                      ? t('connections.enabled')
                      : t(`account.blockedBy.${feature.blockedBy}` as TranslationKey)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function LayerCell({ ok }: { ok: boolean }) {
  return <span className={ok ? 'ok-mark' : 'no-mark'}>{ok ? '✓' : '✕'}</span>;
}

/**
 * A translated name for a catalogue key, falling back to the name the API
 * supplied. See the comment on QUOTA_LABELS for why the fallback exists.
 */
function label(
  t: (key: TranslationKey) => string,
  dictionary: Record<string, TranslationKey>,
  key: string,
  apiName: string | undefined,
): string {
  const translationKey = dictionary[key];
  return translationKey ? t(translationKey) : (apiName ?? key);
}

/**
 * Changing your own password.
 *
 * On the overview rather than buried under Users, because the person most
 * likely to want it is the one reading their own account page — and because
 * the Users tab is where you change *other* people, which is a different act.
 */
function ChangeOwnPassword() {
  const { t } = useI18n();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);

  const change = useMutation({
    mutationFn: () => api.changeOwnPassword(current, next),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('account.passwordChanged') });
      setCurrent('');
      setNext('');
    },
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('common.requestFailed'),
      }),
  });

  return (
    <div className="card composer" style={{ marginTop: 24 }}>
      <h3>{t('account.changePassword')}</h3>

      <label htmlFor="own-current">{t('account.currentPassword')}</label>
      <input
        id="own-current"
        type="password"
        className="ltr"
        autoComplete="current-password"
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
      />

      <label htmlFor="own-next">{t('account.newPassword')}</label>
      <input
        id="own-next"
        type="password"
        className="ltr"
        autoComplete="new-password"
        minLength={6}
        value={next}
        onChange={(event) => setNext(event.target.value)}
      />

      {notice && <p className={notice.kind === 'ok' ? 'ok small' : 'error small'}>{notice.text}</p>}

      <button
        type="button"
        className="primary"
        disabled={change.isPending || !current || next.length < 6}
        onClick={() => change.mutate()}
      >
        {t('common.save')}
      </button>
    </div>
  );
}

/**
 * The pages one colleague looks after.
 *
 * The decision this screen carries out: a communication operator sees only the
 * pages assigned to them — their inbox, their comments, their conversations,
 * and nothing from the accounts they do not work on.
 *
 * Ticked boxes and one Save, rather than a control that grants on click. An
 * administrator sorting out who covers which brand does it in one pass, and a
 * half-applied change is exactly the state where somebody keeps access they
 * were meant to lose.
 *
 * Not offered for an administrator at all: the role reaches every page whatever
 * this table says, and a screen showing an administrator ticked into two pages
 * would be a screen that lies.
 */
function AssignedPages({
  userId,
  onNotice,
}: {
  userId: string;
  onNotice: (notice: { kind: 'ok' | 'error'; text: string }) => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const pages = useQuery({ queryKey: ['pages'], queryFn: api.pages });
  const assigned = useQuery({
    queryKey: ['user-pages', userId],
    queryFn: () => api.userPages(userId),
  });

  /*
   * `null` until the server answers, and only then seeded. Starting from an
   * empty set would show every box unticked for a moment — and an administrator
   * who pressed Save in that moment would have taken away everything.
   */
  const [chosen, setChosen] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (assigned.data) setChosen(new Set(assigned.data.pageIds));
  }, [assigned.data]);

  const save = useMutation({
    mutationFn: () => api.setUserPages(userId, [...(chosen ?? new Set<string>())]),
    onSuccess: () => {
      onNotice({ kind: 'ok', text: t('account.pages.saved') });
      void queryClient.invalidateQueries({ queryKey: ['user-pages', userId] });
    },
    onError: (error: unknown) =>
      onNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('common.requestFailed'),
      }),
  });

  if (pages.isLoading || assigned.isLoading || chosen === null) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  const all = pages.data?.pages ?? [];

  if (all.length === 0) {
    return <div className="notice">{t('account.pages.none')}</div>;
  }

  const toggle = (pageId: string) => {
    const next = new Set(chosen);
    if (next.has(pageId)) next.delete(pageId);
    else next.add(pageId);
    setChosen(next);
  };

  return (
    <div className="assign-pages">
      <p className="tiny muted">{t('account.pages.hint')}</p>

      <div className="check-list">
        {all.map((page) => (
          <label key={page.id} className="check-row">
            <input type="checkbox" checked={chosen.has(page.id)} onChange={() => toggle(page.id)} />
            <span>{page.name}</span>
          </label>
        ))}
      </div>

      <div className="actions">
        <button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? t('common.saving') : t('common.save')}
        </button>
        {/* Said plainly, because an empty list is a real choice here and not a
            mistake — it is how somebody is taken off every page at once. */}
        {chosen.size === 0 && <span className="tiny muted">{t('account.pages.empty')}</span>}
      </div>
    </div>
  );
}

function Users() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [form, setForm] = useState({
    email: '',
    displayName: '',
    password: '',
    role: 'OPERATOR' as 'ADMIN' | 'PUBLISHER' | 'OPERATOR' | 'VIEWER',
  });
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: api.users });

  // Which row is open for editing. One at a time: two open forms invite typing
  // a password into the wrong person's row.
  const [editing, setEditing] = useState<string | null>(null);
  /** Which row has its page list open. Also one at a time, and for the same reason. */
  const [assigning, setAssigning] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    email: '',
    displayName: '',
    password: '',
  });

  const onError = (error: unknown) =>
    setNotice({
      kind: 'error',
      text: error instanceof ApiError ? error.message : t('common.requestFailed'),
    });

  const create = useMutation({
    mutationFn: () => api.createUser(form),
    onSuccess: () => {
      setForm({ email: '', displayName: '', password: '', role: 'OPERATOR' });
      setNotice({ kind: 'ok', text: t('account.userCreated') });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['account'] });
    },
    onError,
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.updateUser(id, patch),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('account.userUpdated') });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    // The API refuses to deactivate or demote the last active administrator.
    onError,
  });

  if (users.isError) return <div className="notice error">{t('account.adminOnly')}</div>;
  if (users.isLoading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <>
      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      <div className="grid-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('account.user')}</th>
              <th>{t('account.role')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.data?.users.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>{user.displayName}</strong>
                  <br />
                  <span className="mono tiny muted">{user.email}</span>
                </td>
                <td>
                  <select
                    value={user.role}
                    style={{ maxWidth: 150 }}
                    onChange={(event) =>
                      update.mutate({
                        id: user.id,
                        patch: { role: event.target.value },
                      })
                    }
                  >
                    <option value="ADMIN">{t('account.role.ADMIN')}</option>
                    <option value="PUBLISHER">{t('account.role.PUBLISHER')}</option>
                    <option value="OPERATOR">{t('account.role.OPERATOR')}</option>
                    <option value="VIEWER">{t('account.role.VIEWER')}</option>
                  </select>
                </td>
                <td>
                  <span className={`badge ${user.isActive ? 'ok' : 'warn'}`}>
                    {user.isActive ? t('account.activeUser') : t('account.inactiveUser')}
                  </span>
                </td>
                <td>
                  <div className="actions">
                    <button
                      type="button"
                      className={user.isActive ? 'danger small' : 'small'}
                      onClick={() =>
                        update.mutate({
                          id: user.id,
                          patch: { isActive: !user.isActive },
                        })
                      }
                    >
                      {user.isActive ? t('account.deactivate') : t('account.activate')}
                    </button>
                    {user.role !== 'ADMIN' && user.role !== 'SUPERADMIN' && (
                      <button
                        type="button"
                        className="ghost small"
                        onClick={() => setAssigning(assigning === user.id ? null : user.id)}
                      >
                        {t('account.pages.assign')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="ghost small"
                      onClick={() => {
                        const opening = editing !== user.id;
                        setEditing(opening ? user.id : null);
                        // Seeded from the row so the fields open showing what
                        // is there now, and an empty one always means "leave it".
                        setDraft(
                          opening
                            ? {
                                email: user.email,
                                displayName: user.displayName,
                                password: '',
                              }
                            : { email: '', displayName: '', password: '' },
                        );
                      }}
                    >
                      {t('common.edit')}
                    </button>
                  </div>

                  {assigning === user.id && <AssignedPages userId={user.id} onNotice={setNotice} />}

                  {editing === user.id && (
                    <div className="inline-edit">
                      <label htmlFor={`e-name-${user.id}`}>{t('account.displayName')}</label>
                      <input
                        id={`e-name-${user.id}`}
                        value={draft.displayName}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            displayName: event.target.value,
                          })
                        }
                      />

                      <label htmlFor={`e-mail-${user.id}`}>{t('login.email')}</label>
                      <input
                        id={`e-mail-${user.id}`}
                        type="email"
                        className="ltr"
                        value={draft.email}
                        onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                      />
                      {/* The address is where a reset code goes. A wrong one
                          here is what locks somebody out permanently. */}
                      <p className="tiny muted">{t('account.emailHint')}</p>

                      <label htmlFor={`e-pass-${user.id}`}>{t('account.newPassword')}</label>
                      <input
                        id={`e-pass-${user.id}`}
                        type="password"
                        className="ltr"
                        autoComplete="new-password"
                        minLength={6}
                        placeholder={t('account.leaveBlank')}
                        value={draft.password}
                        onChange={(event) => setDraft({ ...draft, password: event.target.value })}
                      />

                      <div className="actions">
                        <button
                          type="button"
                          className="primary small"
                          disabled={
                            update.isPending ||
                            !draft.email.includes('@') ||
                            draft.displayName.length < 1 ||
                            (draft.password.length > 0 && draft.password.length < 6)
                          }
                          onClick={() => {
                            update.mutate({
                              id: user.id,
                              patch: {
                                displayName: draft.displayName,
                                email: draft.email,
                                // Omitted when blank, so saving a name change
                                // does not demand a password nobody wanted to
                                // change.
                                ...(draft.password ? { password: draft.password } : {}),
                              },
                            });
                            setEditing(null);
                          }}
                        >
                          {t('common.save')}
                        </button>
                        <button
                          type="button"
                          className="ghost small"
                          onClick={() => setEditing(null)}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card composer">
        <h3>{t('account.addUser')}</h3>

        <label htmlFor="u-name">{t('account.displayName')}</label>
        <input
          id="u-name"
          value={form.displayName}
          onChange={(event) => setForm({ ...form, displayName: event.target.value })}
        />

        <label htmlFor="u-email">{t('login.email')}</label>
        <input
          id="u-email"
          type="email"
          className="ltr"
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
        />

        <label htmlFor="u-pass">{t('login.password')}</label>
        <input
          id="u-pass"
          type="password"
          className="ltr"
          value={form.password}
          onChange={(event) => setForm({ ...form, password: event.target.value })}
        />
        <p className="muted tiny">{t('account.passwordHint')}</p>

        <label htmlFor="u-role">{t('account.role')}</label>
        <select
          id="u-role"
          value={form.role}
          onChange={(event) => setForm({ ...form, role: event.target.value as typeof form.role })}
        >
          <option value="ADMIN">{t('account.role.ADMIN')}</option>
          <option value="PUBLISHER">{t('account.role.PUBLISHER')}</option>
          <option value="OPERATOR">{t('account.role.OPERATOR')}</option>
          <option value="VIEWER">{t('account.role.VIEWER')}</option>
        </select>

        <button
          type="button"
          className="primary"
          onClick={() => create.mutate()}
          disabled={
            !form.email || !form.displayName || form.password.length < 6 || create.isPending
          }
        >
          <Icon.plus />
          {t('account.addUser')}
        </button>
      </div>
    </>
  );
}

/**
 * Issue a key, and say exactly what it may do.
 *
 * The matrix is the screen. A key used to be a name and nothing else — every
 * key could do everything the account could, which is fine while one person
 * holds one key and indefensible the moment an agent is given one. What is
 * built here is a grid: a row per Page, a column per capability, and a tick in
 * a cell means "this key, this Page, this thing".
 *
 * Two decisions are visible in the layout rather than explained in prose:
 *
 *  - **Every Page** is its own row at the top, not a checkbox beside the
 *    others. It is a different kind of claim — it covers Pages that do not
 *    exist yet — and putting it in line with the specific Pages would make the
 *    broadest grant the easiest one to tick by accident.
 *
 *  - **Send without review** is a separate control that only appears once
 *    "Answer the inbox" is ticked, and it is off. It is the one setting on this
 *    screen that puts unread words in front of a customer, and it should cost a
 *    deliberate second click.
 */
function ApiKeys() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const fmt = useFormatters();
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [issued, setIssued] = useState<string | null>(null);

  /*
   * The matrix as it is being built, keyed `${pageScope}:${capability}`.
   *
   * A flat set rather than a nested object: a tick is either present or it is
   * not, and every read on this screen is "is this cell on", which a Set answers
   * without walking anything.
   */
  const [ticks, setTicks] = useState<Set<string>>(new Set());
  const [autoSend, setAutoSend] = useState<Set<string>>(new Set());

  const keys = useQuery({ queryKey: ['api-keys'], queryFn: api.apiKeys });
  const pages = useQuery({ queryKey: ['pages'], queryFn: api.pages });

  const livePages = (pages.data?.pages ?? []).filter((page) => page.isActive);

  const cell = (scope: string, capability: ApiCapability) => `${scope}:${capability}`;

  function toggle(scope: string, capability: ApiCapability): void {
    setTicks((current) => {
      const next = new Set(current);
      const id = cell(scope, capability);
      if (next.has(id)) {
        next.delete(id);
        // Untick the reply capability and the review setting goes with it —
        // leaving it behind would carry a decision the operator just withdrew.
        if (capability === 'INBOX_REPLY') {
          setAutoSend((sending) => {
            const kept = new Set(sending);
            kept.delete(scope);
            return kept;
          });
        }
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const grants: ApiKeyGrantInput[] = [];
  for (const scope of [ALL_PAGES, ...livePages.map((page) => page.id)]) {
    for (const capability of API_CAPABILITIES) {
      if (!ticks.has(cell(scope, capability))) continue;
      grants.push({
        pageScope: scope,
        capability,
        autoSend: capability === 'INBOX_REPLY' && autoSend.has(scope),
      });
    }
  }

  const create = useMutation({
    mutationFn: () =>
      api.createApiKey({
        name,
        grants,
        ...(expiresInDays ? { expiresInDays: Number(expiresInDays) } : {}),
      }),
    onSuccess: (data) => {
      // Shown once, and only once — the server keeps a hash, not the key.
      setIssued(data.key.rawKey);
      setName('');
      setExpiresInDays('');
      setTicks(new Set());
      setAutoSend(new Set());
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revoke = useMutation({
    mutationFn: api.revokeApiKey,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  /*
   * Held in state rather than shown inline in the row.
   *
   * The key and the connector address are the same secret, and a table cell
   * that always displays it puts a working credential on screen behind whoever
   * walks past. Asking for it is one click; keeping it there is not a default.
   */
  const [revealed, setRevealed] = useState<{ rawKey: string; connectorUrl: string } | null>(null);

  const reveal = useMutation({
    mutationFn: api.revealApiKey,
    onSuccess: (result) => setRevealed(result),
  });

  const rows: Array<{ scope: string; label: string; hint: string | null }> = [
    { scope: ALL_PAGES, label: t('account.keyAllPages'), hint: t('account.keyAllPagesHint') },
    ...livePages.map((page) => ({ scope: page.id, label: page.name, hint: null })),
  ];

  return (
    <>
      <p className="muted small">{t('account.keysHint')}</p>

      {issued && (
        <div className="notice ok">
          <div>
            <p style={{ margin: 0 }}>
              <strong>{t('account.keyIssued')}</strong>
            </p>
            <code className="mono" style={{ wordBreak: 'break-all' }}>
              {issued}
            </code>
            <p className="tiny muted" style={{ margin: '6px 0 0' }}>
              {t('account.keyOnce')}
            </p>
          </div>
        </div>
      )}

      {create.error && <ErrorNotice error={create.error} />}
      {reveal.error && <ErrorNotice error={reveal.error} />}

      {revealed && (
        <div className="notice ok">
          <div>
            <p style={{ margin: 0 }}>
              <strong>{t('account.keyShown')}</strong>
            </p>
            <p className="tiny muted" style={{ margin: '6px 0 2px' }}>
              {t('account.keyValue')}
            </p>
            <code className="mono" style={{ wordBreak: 'break-all' }}>
              {revealed.rawKey}
            </code>
            <p className="tiny muted" style={{ margin: '10px 0 2px' }}>
              {t('account.keyConnectorUrl')}
            </p>
            <code className="mono" style={{ wordBreak: 'break-all' }}>
              {revealed.connectorUrl}
            </code>
            <p className="tiny muted" style={{ margin: '8px 0 0' }}>
              {t('account.keyConnectorHint')}
            </p>
            <button
              type="button"
              className="small"
              style={{ marginTop: 8 }}
              onClick={() => setRevealed(null)}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      <div className="inline-form">
        <input
          type="text"
          placeholder={t('account.keyName')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <input
          type="number"
          min={1}
          max={3650}
          placeholder={t('account.keyExpiry')}
          value={expiresInDays}
          onChange={(event) => setExpiresInDays(event.target.value)}
        />
      </div>

      <h4 style={{ margin: '18px 0 4px' }}>{t('account.keyMatrix')}</h4>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t('account.keyMatrixHint')}
      </p>

      {livePages.length === 0 && !pages.isLoading ? (
        <p className="muted small">{t('account.keyNoPages')}</p>
      ) : (
        <div className="grid-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th />
                {API_CAPABILITIES.map((capability) => (
                  <th key={capability} className="small">
                    {t(`account.keyCap.${capability}` as TranslationKey)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.scope}>
                  <td>
                    {row.label}
                    {row.hint && (
                      <div className="tiny muted" style={{ maxWidth: '22rem' }}>
                        {row.hint}
                      </div>
                    )}
                    {ticks.has(cell(row.scope, 'INBOX_REPLY')) && (
                      <label
                        className="tiny"
                        style={{ display: 'block', marginTop: 6, fontWeight: 500 }}
                      >
                        <input
                          type="checkbox"
                          checked={autoSend.has(row.scope)}
                          onChange={(event) =>
                            setAutoSend((current) => {
                              const next = new Set(current);
                              if (event.target.checked) next.add(row.scope);
                              else next.delete(row.scope);
                              return next;
                            })
                          }
                        />{' '}
                        {t('account.keyAutoSend')}
                        <div className="tiny muted" style={{ maxWidth: '22rem' }}>
                          {t('account.keyAutoSendHint')}
                        </div>
                      </label>
                    )}
                  </td>
                  {API_CAPABILITIES.map((capability) => (
                    <td key={capability} style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        aria-label={`${row.label} — ${t(`account.keyCap.${capability}` as TranslationKey)}`}
                        checked={ticks.has(cell(row.scope, capability))}
                        onChange={() => toggle(row.scope, capability)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="inline-form" style={{ marginTop: 12 }}>
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={!name || grants.length === 0 || create.isPending}
        >
          <Icon.plus />
          {t('account.createKey')}
        </button>
        {name && grants.length === 0 && (
          <span className="tiny muted">{t('account.keyNoGrants')}</span>
        )}
      </div>

      {(keys.data?.keys.length ?? 0) > 0 && (
        <div className="grid-wrap" style={{ marginTop: 20 }}>
          <table className="grid">
            <thead>
              <tr>
                <th>{t('automation.agentName')}</th>
                <th>{t('account.keyPrefix')}</th>
                <th>{t('account.keyGrantsColumn')}</th>
                <th>{t('account.keyExpiresColumn')}</th>
                <th>{t('account.lastUsed')}</th>
                <th>{t('common.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.data?.keys.map((key) => {
                const expired =
                  !key.revokedAt && key.expiresAt !== null && new Date(key.expiresAt) <= new Date();
                return (
                  <tr key={key.id}>
                    <td>{key.name}</td>
                    <td className="mono small">{key.prefix}…</td>
                    <td className="small">
                      {/*
                       * Grouped by Page rather than listed flat. A key with four
                       * capabilities on three Pages is twelve rows, and twelve
                       * rows is a wall nobody reads — which defeats the point of
                       * showing it on the screen where access is audited.
                       */}
                      {groupGrants(key.grants).map((group) => (
                        <div key={group.scope}>
                          <strong>
                            {group.scope === ALL_PAGES
                              ? t('account.keyAllPages')
                              : (group.name ?? group.scope)}
                          </strong>
                          {': '}
                          {group.capabilities
                            .map((capability) =>
                              t(`account.keyCap.${capability}` as TranslationKey),
                            )
                            .join('، ')}
                          {group.autoSend && (
                            <span className="badge warn" style={{ marginInlineStart: 6 }}>
                              {t('account.keyAutoSend')}
                            </span>
                          )}
                        </div>
                      ))}
                      {key.grants.length === 0 && <span className="muted">—</span>}
                    </td>
                    <td className="small">
                      {/*
                       * "Never" rather than a blank or a far-future date. A key
                       * with no end is a deliberate choice on this screen, and
                       * showing nothing would read as a value that failed to
                       * load.
                       */}
                      {key.expiresAt ? fmt.dateTime(key.expiresAt) : t('account.keyNoExpiry')}
                    </td>
                    <td className="small">
                      {key.lastUsedAt ? fmt.dateTime(key.lastUsedAt) : t('common.never')}
                    </td>
                    <td>
                      <span className={`badge ${key.revokedAt || expired ? 'err' : 'ok'}`}>
                        {key.revokedAt
                          ? t('account.revoked')
                          : expired
                            ? t('account.keyExpired')
                            : t('account.activeUser')}
                      </span>
                    </td>
                    <td>
                      {key.canReveal && (
                        <button
                          type="button"
                          className="small"
                          onClick={() => reveal.mutate(key.id)}
                          disabled={reveal.isPending}
                        >
                          {t('account.keyShow')}
                        </button>
                      )}
                      {!key.revokedAt && (
                        <button
                          type="button"
                          className="danger small"
                          onClick={() => revoke.mutate(key.id)}
                        >
                          {t('account.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** One line per Page instead of one per cell, so a wide key is still readable. */
function groupGrants(
  grants: Array<{
    pageScope: string;
    pageName: string | null;
    capability: ApiCapability;
    autoSend: boolean;
  }>,
): Array<{ scope: string; name: string | null; capabilities: ApiCapability[]; autoSend: boolean }> {
  const groups = new Map<
    string,
    { scope: string; name: string | null; capabilities: ApiCapability[]; autoSend: boolean }
  >();

  for (const grant of grants) {
    const existing = groups.get(grant.pageScope);
    if (existing) {
      existing.capabilities.push(grant.capability);
      existing.autoSend ||= grant.autoSend;
    } else {
      groups.set(grant.pageScope, {
        scope: grant.pageScope,
        name: grant.pageName,
        capabilities: [grant.capability],
        autoSend: grant.autoSend,
      });
    }
  }

  // The wildcard first: it is the broadest claim, and the one somebody auditing
  // this list most needs to see before anything else.
  return [...groups.values()].sort((a, b) =>
    a.scope === ALL_PAGES ? -1 : b.scope === ALL_PAGES ? 1 : a.scope.localeCompare(b.scope),
  );
}

/**
 * The tenant's own Meta app.
 *
 * Sold on the PRO and ENTERPRISE plans since the catalogue shipped, and until
 * now reachable only by writing to the database: the service behind it was
 * complete and no route or screen ever called it.
 *
 * The platform app is listed alongside and cannot be edited. Showing it is the
 * point — otherwise a tenant with no app of their own sees an empty list and
 * cannot tell whether that means "nothing is connecting" or "the platform's app
 * is doing it".
 */
function MetaApps() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const fmt = useFormatters();

  const [label, setLabel] = useState('');
  const [externalAppId, setExternalAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [loginConfigId, setLoginConfigId] = useState('');

  const apps = useQuery({ queryKey: ['meta-apps'], queryFn: api.metaApps });

  const reset = () => {
    setLabel('');
    setExternalAppId('');
    setAppSecret('');
    setLoginConfigId('');
  };

  const create = useMutation({
    mutationFn: () =>
      api.createMetaApp({
        label,
        externalAppId,
        appSecret,
        loginConfigId: loginConfigId || undefined,
      }),
    onSuccess: () => {
      reset();
      void queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
    },
  });

  const remove = useMutation({
    mutationFn: api.deleteMetaApp,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meta-apps'] }),
  });

  const own = (apps.data?.apps ?? []).filter((row) => row.provider === 'TENANT');
  const platform = (apps.data?.apps ?? []).find((row) => row.provider === 'PLATFORM');

  return (
    <>
      <p className="muted small">{t('account.appsHint')}</p>

      {apps.isError && <ErrorNotice error={apps.error} />}
      {create.isError && <ErrorNotice error={create.error} />}
      {remove.isError && <ErrorNotice error={remove.error} />}

      {platform && own.length === 0 && (
        <div className="notice">
          <p style={{ margin: 0 }}>{t('account.appsUsingPlatform')}</p>
        </div>
      )}

      {own.length > 0 && (
        <div className="grid-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t('automation.agentName')}</th>
                <th>{t('account.appId')}</th>
                <th>{t('common.status')}</th>
                <th>{t('account.appConnections')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {own.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.label}</strong>
                    {row.isDefault && (
                      <span className="badge ok small">{t('account.appDefault')}</span>
                    )}
                    {/* The callback URL is per app and has to be pasted into
                        Meta's dashboard by hand. Hiding it behind a details
                        page would mean one more place to go mid-setup. */}
                    <div className="tiny muted mono" style={{ wordBreak: 'break-all' }}>
                      {row.webhookCallbackUrl}
                    </div>
                  </td>
                  <td className="mono">{row.externalAppId}</td>
                  <td>
                    <span className={`badge ${row.status === 'ACTIVE' ? 'ok' : 'err'}`}>
                      {row.status === 'ACTIVE' ? t('account.appVerified') : t('account.appInvalid')}
                    </span>
                    {/* Meta's own words. "Invalid" alone does not distinguish a
                        mistyped secret from an app in development mode. */}
                    {row.verifyError && <div className="tiny error">{row.verifyError}</div>}
                    {row.verifiedAt && (
                      <div className="tiny muted">{fmt.dateTime(row.verifiedAt)}</div>
                    )}
                  </td>
                  <td>{row.connectionCount}</td>
                  <td>
                    <button
                      type="button"
                      className="danger small"
                      disabled={row.connectionCount > 0 || remove.isPending}
                      // Refused server-side too. Disabling it here explains why
                      // rather than letting somebody press it and read an error.
                      title={row.connectionCount > 0 ? t('account.appInUse') : undefined}
                      onClick={() => remove.mutate(row.id)}
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card composer">
        <h3>{t('account.addApp')}</h3>
        <p className="tiny muted">{t('account.addAppHint')}</p>

        <label htmlFor="ma-label">{t('account.appLabel')}</label>
        <input id="ma-label" value={label} onChange={(event) => setLabel(event.target.value)} />

        <label htmlFor="ma-id">{t('account.appId')}</label>
        <input
          id="ma-id"
          className="ltr mono"
          inputMode="numeric"
          value={externalAppId}
          onChange={(event) => setExternalAppId(event.target.value)}
        />

        {/* type=password so it is not left on screen over somebody's shoulder.
            It goes straight into the same AES-256-GCM vault as Meta's tokens,
            and no endpoint ever returns it again — not even to this form. */}
        <label htmlFor="ma-secret">{t('account.appSecret')}</label>
        <input
          id="ma-secret"
          type="password"
          className="ltr"
          autoComplete="new-password"
          value={appSecret}
          onChange={(event) => setAppSecret(event.target.value)}
        />

        <label htmlFor="ma-config">{t('account.appLoginConfig')}</label>
        <input
          id="ma-config"
          className="ltr mono"
          inputMode="numeric"
          value={loginConfigId}
          onChange={(event) => setLoginConfigId(event.target.value)}
        />

        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={!label || !externalAppId || !appSecret || create.isPending}
        >
          <Icon.plus />
          {create.isPending ? t('account.appVerifying') : t('account.addApp')}
        </button>
      </div>
    </>
  );
}
